import { useEffect, useState } from "react";
import { EnvelopeSimple, IdentificationCard, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import BadgePhotoEditor from "../components/BadgePhotoEditor";

function PersonCheck({ p, checked, onToggle, note, tid }) {
  return (
    <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${checked ? "border-primary bg-primary/5" : "border-stone-200"} ${note ? "opacity-60" : "cursor-pointer hover:border-primary/50"}`} data-testid={tid}>
      <input type="checkbox" checked={checked} disabled={!!note} onChange={onToggle} className="accent-[#6a3e97]" />
      <span className="flex-1 truncate font-medium text-stone-800">{p.name}</span>
      {note && <span className="text-[10px] font-semibold text-[#8a5d14]">{note}</span>}
    </label>
  );
}

export default function PrintCenterPage() {
  const [tab, setTab] = useState("badges");
  const [badgePeople, setBadgePeople] = useState([]);
  const [adjustUid, setAdjustUid] = useState(null);
  const [zipBusy, setZipBusy] = useState(false);
  const [pickedBadges, setPickedBadges] = useState({});
  const [mail, setMail] = useState({ clients: [], staff: [] });
  const [pickedMail, setPickedMail] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/badges/print-list").then((r) => setBadgePeople(r.data.people || [])).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/mailing-labels/people").then((r) => setMail(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const downloadPdf = async (url, filename) => {
    setBusy(true);
    try {
      const r = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(e.response?.data?.detail ? String(e.response.data.detail) : "Nothing to print for that selection"); }
    finally { setBusy(false); }
  };

  const badgeIds = Object.keys(pickedBadges).filter((k) => pickedBadges[k]);
  const mailSel = pickedMail;
  const clientIds = Object.keys(mailSel).filter((k) => k.startsWith("c:") && mailSel[k]).map((k) => k.slice(2));
  const staffIds = Object.keys(mailSel).filter((k) => k.startsWith("s:") && mailSel[k]).map((k) => k.slice(2));

  return (
    <div className="max-w-4xl space-y-6" data-testid="print-center">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-stone-900">Print center</h1>
        <p className="mt-1 text-sm text-stone-500">Check off who you need, print a sheet. Badges come out at true wallet-card size (CR80), 8 per letter page — cut and laminate. Labels fit standard 30-up address label sheets (Avery 5160).</p>
      </div>

      <div className="flex gap-2" data-testid="print-center-tabs">
        {[["badges", "ID badges", IdentificationCard], ["labels", "Mailing labels", EnvelopeSimple]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-600 hover:border-primary"}`}
            data-testid={`print-tab-${k}`}><Icon size={15} /> {l}</button>
        ))}
      </div>

      {tab === "badges" && (
        <div className="space-y-3" data-testid="badge-print-tab">
          <div className="flex justify-end">
            <button onClick={async () => {
              setZipBusy(true);
              try {
                const r = await api.get("/admin/badges/download-zip", { responseType: "blob", timeout: 300000 });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(r.data);
                a.download = `Silver_Home_Care_Badges_${new Date().toISOString().slice(0, 10)}.zip`;
                a.click();
                URL.revokeObjectURL(a.href);
                toast.success("Badge ZIP downloaded");
              } catch (e) { toast.error(formatApiError(e)); }
              setZipBusy(false);
            }} disabled={zipBusy}
              className="inline-flex h-8 items-center rounded-md border border-stone-300 bg-white px-3 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              data-testid="badges-download-zip">{zipBusy ? "Preparing ZIP… (about 30 seconds)" : "Download all badges (ZIP)"}</button>
          </div>
          {badgePeople.some((p) => p.pending_badge_id) && (
            <div className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3" data-testid="badge-pending-review">
              <p className="text-xs font-bold uppercase tracking-wide text-[#8a5d14]">
                New badges from license photos — look them over, approve, then they print
              </p>
              <button onClick={async () => {
                const pending = badgePeople.filter((p) => p.pending_badge_id);
                for (const p of pending) {
                  try { await api.post(`/employees/${p.user_id}/badges/${p.pending_badge_id}/approve`); }
                  catch (e) { toast.error(formatApiError(e)); }
                }
                toast.success(`Approved ${pending.length} badges`);
                api.get("/admin/badges/print-list").then(({ data }) => setBadgePeople(data.people || []));
              }} className="mt-1 rounded-full bg-primary px-3 py-1 text-[11px] font-bold text-primary-foreground"
                data-testid="badge-approve-all">Approve all ({badgePeople.filter((p) => p.pending_badge_id).length})</button>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
                {badgePeople.filter((p) => p.pending_badge_id).map((p) => (
                  <div key={p.user_id} className="rounded-lg border border-stone-200 bg-white p-2 text-center" data-testid={`badge-pending-${p.user_id}`}>
                    <img src={`${api.defaults.baseURL}/users/${p.user_id}/documents/badge_photo`} alt={p.name}
                      className="mx-auto h-20 w-16 rounded-md border border-stone-200 object-cover" loading="lazy" />
                    <p className="mt-1 truncate text-[11px] font-semibold text-stone-700">{p.name}</p>
                    <div className="mt-1 flex items-center justify-center gap-1.5">
                      <button onClick={() => setAdjustUid(p.user_id)}
                        className="rounded-full border border-stone-300 bg-white px-2.5 py-0.5 text-[10px] font-bold text-stone-600 hover:bg-stone-50"
                        data-testid={`badge-adjust-${p.user_id}`}>Adjust</button>
                      <button onClick={async () => {
                        try {
                          await api.post(`/employees/${p.user_id}/badges/${p.pending_badge_id}/approve`);
                          toast.success(`Approved — ${p.name}`);
                          setBadgePeople((s) => s.map((x) => x.user_id === p.user_id ? { ...x, pending_badge_id: "", has_badge: true } : x));
                        } catch (e) { toast.error(formatApiError(e)); }
                      }} className="rounded-full bg-primary px-3 py-0.5 text-[10px] font-bold text-primary-foreground"
                        data-testid={`badge-approve-${p.user_id}`}>Approve</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
            {badgePeople.map((p) => (
              <PersonCheck key={p.user_id} p={p} tid={`badge-check-${p.user_id}`}
                checked={!!pickedBadges[p.user_id]}
                note={p.has_badge ? "" : "no badge yet"}
                onToggle={() => setPickedBadges((s) => ({ ...s, [p.user_id]: !s[p.user_id] }))} />
            ))}
          </div>
          <button onClick={() => downloadPdf(`/admin/badges/print-sheet?uids=${badgeIds.join(",")}`, "badge-sheet.pdf")}
            disabled={busy || !badgeIds.length}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50" data-testid="badge-print-button">
            <Printer size={16} /> Print badge sheet ({badgeIds.length})
          </button>
          <p className="text-xs text-stone-400">Only people with a generated badge can be checked. Make badges on their profile page first.</p>
        </div>
      )}

      {tab === "labels" && (
        <div className="space-y-4" data-testid="label-print-tab">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Clients</p>
            <div className="mt-2 grid max-h-56 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2 md:grid-cols-3">
              {mail.clients.map((p) => (
                <PersonCheck key={p.id} p={p} tid={`mail-client-${p.id}`}
                  checked={!!mailSel[`c:${p.id}`]}
                  note={p.address ? "" : "no address"}
                  onToggle={() => setPickedMail((s) => ({ ...s, [`c:${p.id}`]: !s[`c:${p.id}`] }))} />
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Employees</p>
            <div className="mt-2 grid max-h-56 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2 md:grid-cols-3">
              {mail.staff.map((p) => (
                <PersonCheck key={p.id} p={p} tid={`mail-staff-${p.id}`}
                  checked={!!mailSel[`s:${p.id}`]}
                  note={p.address ? "" : "no address"}
                  onToggle={() => setPickedMail((s) => ({ ...s, [`s:${p.id}`]: !s[`s:${p.id}`] }))} />
              ))}
            </div>
          </div>
          <button onClick={() => downloadPdf(`/admin/mailing-labels?clients=${clientIds.join(",")}&staff=${staffIds.join(",")}`, "mailing-labels.pdf")}
            disabled={busy || (!clientIds.length && !staffIds.length)}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50" data-testid="label-print-button">
            <Printer size={16} /> Print labels ({clientIds.length + staffIds.length})
          </button>
          <p className="text-xs text-stone-400">People without an address on file can't be checked — add addresses on their profile/client file.</p>
        </div>
      )}
      {adjustUid && (
        <BadgePhotoEditor uid={adjustUid} open={!!adjustUid} onClose={() => setAdjustUid(null)}
          onSaved={async () => {
            try {
              await api.post(`/employees/${adjustUid}/badges`, { variant: "dcw", photo_source: "current" });
              toast.success("Badge re-rendered with the adjusted photo");
            } catch (e) { toast.error(formatApiError(e)); }
            setAdjustUid(null);
            api.get("/admin/badges/print-list").then(({ data }) => setBadgePeople(data.people || []));
          }} />
      )}
    </div>
  );
}
